import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useConfigs } from '@/hooks/use-api';
import { useAuth } from '@/hooks/use-api';
import { Button } from '@/components/ui/button';
import LoadingSpinner from '@/components/LoadingSpinner';
import { CheckCircle, XCircle, Plus, Settings, Database } from 'lucide-react';

const DashboardPage: React.FC = () => {
  const { user, logout, isLoggingOut, isAuthenticated } = useAuth();
  const [showLogoutDialog, setShowLogoutDialog] = React.useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6; // 2 rows of 3 cards each

  const {
    data: configsResponse,
    isLoading,
    error,
    refetch,
  } = useConfigs(isAuthenticated && !isLoggingOut);
  // Extract configs array from response
  const configs = configsResponse?.data?.configs || [];

  // Only refetch on first load, not on navigation (use cache instead)
  useEffect(() => {
    if (isAuthenticated && !isLoggingOut && !configs) {
      refetch();
    }
  }, [isAuthenticated, isLoggingOut, refetch]);

  // Calculate pagination
  const totalPages = configs ? Math.ceil(configs.length / itemsPerPage) : 0;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentConfigs = configs ? configs.slice(startIndex, endIndex) : [];

  // Reset to page 1 when configs change
  React.useEffect(() => {
    if (configs && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [configs, currentPage, totalPages]);

  const getStatusIcon = (handshakeCompleted: boolean) => {
    if (handshakeCompleted) {
      return (
        <CheckCircle
          className="w-5 h-5"
          style={{ color: 'rgb(25, 185, 82)' }}
        />
      );
    }
    return <XCircle className="w-5 h-5 text-red-500" />;
  };

  const getStatusText = (handshakeCompleted: boolean) => {
    return handshakeCompleted ? 'Connected' : 'Not Connected';
  };

  const getStatusColor = (handshakeCompleted: boolean) => {
    return handshakeCompleted ? 'badge-success' : 'badge-error';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && !isLoggingOut) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-destructive mb-4">
            Error Loading Configurations
          </h2>
          <p className="text-muted-foreground mb-4">
            {(error as any)?.message || 'Failed to load sender configurations'}
          </p>
          <Button onClick={() => window.location.reload()}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-background flex flex-col relative">
      {/* Header */}
      <header className="bg-white shadow-sm border-b header-border shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <img
                  src="/onextel-logo-white.png"
                  alt="Onextel Logo"
                  className="h-12 w-auto object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-primary">
                  Optimove - Onextel Setup
                </h1>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-semibold text-primary">
                  {user?.email}
                </p>
                <p className="text-xs text-muted">Administrator</p>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowLogoutDialog(true)}
                className="btn-outline"
              >
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-primary mb-2">
                  Sender Configurations
                </h2>
                <p className="text-base text-muted">
                  Manage your SMS connector configurations
                </p>
              </div>
              <Link to="/configs/new">
                <Button className="btn-primary h-10 px-5 text-sm">
                  <Plus className="w-4 h-4 mr-2" />
                  New Configuration
                </Button>
              </Link>
            </div>
          </div>

          {/* Configurations List */}
          {configs && configs.length > 0 ? (
            <>
              <div className="grid gap-9 md:grid-cols-2 lg:grid-cols-3 mb-6">
                {currentConfigs.map((config) => (
                  <div
                    key={config._id}
                    className="p-3 hover:scale-105 transition-all duration-300 border rounded-xl shadow-lg card-onextel"
                  >
                    <div className="flex flex-col mb-2 gap-1">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 mr-2 min-w-0">
                          <p className="text-sm font-bold text-primary break-words leading-snug">
                            <span className="font-normal text-muted-foreground">
                              Sender ID:{' '}
                            </span>
                            {config.senderId}
                          </p>
                        </div>
                        <div
                          className={`flex items-center space-x-1 px-2 py-1 rounded-full border text-xs font-bold shrink-0 ${getStatusColor(config.handshakeCompleted)}`}
                        >
                          {getStatusIcon(config.handshakeCompleted)}
                          <span>
                            {getStatusText(config.handshakeCompleted)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-1 mb-2">
                      <div className="flex items-center text-xs">
                        <span className="text-muted-foreground">Provider:</span>
                        <span className="ml-2 font-medium text-foreground">
                          Onextel
                        </span>
                      </div>
                      <div className="flex items-center text-xs">
                        <span className="text-muted-foreground">API Key:</span>
                        <span className="ml-2 font-mono text-foreground">
                          **********
                        </span>
                      </div>
                    </div>

                    <div className="space-y-1 mb-4">
                      <div className="flex items-center text-xs">
                        <span className="text-muted-foreground">URL:</span>
                        <span className="ml-2 font-medium truncate flex-1 text-foreground">
                          Configured
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground font-semibold">
                          Created:
                        </span>
                        <span className="text-foreground font-medium">
                          {new Date(config.createdAt).toLocaleDateString()}
                        </span>
                      </div>

                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground font-semibold">
                          Last Updated:
                        </span>
                        <span className="text-foreground font-medium">
                          {new Date(config.updatedAt).toLocaleDateString()}
                        </span>
                      </div>

                      {config.handshakeCompletedAt && (
                        <div className="flex justify-between text-xs">
                          <span className="text-muted-foreground font-semibold">
                            Connected:
                          </span>
                          <span className="text-foreground font-medium">
                            {new Date(
                              config.handshakeCompletedAt
                            ).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-1">
                      <Link to={`/configs/${config._id}`}>
                        <Button
                          variant="outline"
                          size="sm"
                          className="btn-outline flex-1 py-1 text-xs"
                        >
                          <Settings className="w-3 h-3 mr-1" />
                          View Details
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-4 mb-8">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className="btn-outline"
                  >
                    Previous
                  </Button>

                  <span className="text-sm text-muted">
                    Page {currentPage} of {totalPages}
                  </span>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="btn-outline"
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-light rounded-full mb-6 animate-float">
                <Database className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-primary mb-3">
                No Configurations Yet
              </h3>
              <p className="text-muted mb-6">
                Get started by creating your first SMS connector configuration
              </p>
              <p className="text-sm text-muted">
                Use the "New Configuration" button above to get started
              </p>
            </div>
          )}

          {/* Logout Confirmation Dialog */}
          {showLogoutDialog && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 card-onextel">
                <h3 className="text-xl font-bold text-primary mb-4">
                  Confirm Logout
                </h3>
                <p className="text-muted mb-6">
                  Are you sure you want to sign out of your account?
                </p>
                <div className="flex space-x-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowLogoutDialog(false)}
                    className="btn-outline flex-1"
                  >
                    Cancel
                  </Button>
                  <Button onClick={logout} className="btn-primary flex-1">
                    Sign Out
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
